import {
  Button,
  CheckCircleIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  DebouncedTextField,
  EnvelopeIcon,
  Flex,
  InputContainer,
  Menu,
  Spacer,
  Text,
} from 'components'
import { ReactNode, useState } from 'react'
import { QRCodeConfiguration, QRCodeProvider } from 'types'
import { sendIntercomMessage, testQrConnection, useEntevatePermission } from 'utils'
import { useEventInfo } from '../../../pages/EventInfoPage'
import { isQrCodeConfigurationValid } from '../../../utils'
import QrMappingDrawer from './QrMappingDrawer'

interface Props {
  qrCode?: QRCodeConfiguration
  onProviderChange?: (value: QRCodeProvider | 'not-listed') => void
  onConfigurationChange?: (value: Partial<QRCodeConfiguration>) => void
  eventTitle?: string
}

export default function QrLink({
  qrCode,
  onProviderChange,
  onConfigurationChange,
  eventTitle,
}: Props) {
  const [entevatePermission] = useEntevatePermission()
  const { updateShowFieldMapping } = useEventInfo()

  const [authKeyError, setAuthKeyError] = useState('')
  const [actCodeError, setActCodeError] = useState('')
  const [exhibitorBadgeIdError, setExhibitorBadgeIdError] = useState('')
  const [exhibitorIdError, setExhibitorIdError] = useState('')
  const [eventCodeError, setEventCodeError] = useState('')
  const [loading, setLoading] = useState(false)

  const readyForAuthentication = isQrCodeConfigurationValid(qrCode, true)
  const { isValid, leadMapping } = qrCode || {}

  function handleAPIAuth() {
    clearErrorMessages()
    setLoading(true)
    authenticateApi()
  }

  async function authenticateApi() {
    if (!qrCode) return

    try {
      await testQrConnection(qrCode)
      setLoading(false)
      onConfigurationChange?.({ isValid: true })
    } catch (error) {
      setLoading(false)
      setErrorMessage(String(error))
      onConfigurationChange?.({ isValid: false })
    }
  }

  function setErrorMessage(apiError: string) {
    if (apiError.includes('Invalid authentication key'))
      setAuthKeyError('Authentication Key is incorrect, please try again.')
    if (apiError.includes('Unauthorized'))
      setAuthKeyError('API Key is incorrect or expired, please try again.')
    if (apiError.includes('Activation Code'))
      setActCodeError('Activation code is incorrect, please try again.')
    if (apiError.includes('Exhibitor Badge ID'))
      setExhibitorBadgeIdError('Exhibitor Badge ID is incorrect, please try again.')
    if (apiError.includes('key has expired') || apiError.includes('exhibitor ID'))
      setExhibitorIdError('Exhibitor Id is incorrect, please try again.')
    if (apiError.includes('Invalid event code'))
      setEventCodeError('Event code is incorrect, please try again.')
  }

  function clearErrorMessages() {
    setAuthKeyError('')
    setActCodeError('')
    setExhibitorBadgeIdError('')
    setExhibitorIdError('')
    setEventCodeError('')
  }

  return (
    <Flex direction="column" gap={5}>
      <InputContainer label="Select a Service">
        <Menu
          variant="outline"
          borderRadius="10px"
          menuProps={{ positioning: { sameWidth: true } }}
          options={getProviders(entevatePermission === 'entevate')}
          value={qrCode?.type || 'not-listed'}
          onChange={onProviderChange}
        />
      </InputContainer>

      <Flex direction="column" gap={3}>
        <ConfigurationOptionsRenderer
          qrCode={qrCode}
          onConfigurationChange={onConfigurationChange}
          authKeyError={authKeyError}
          actCodeError={actCodeError}
          exhibitorBadgeIdError={exhibitorBadgeIdError}
          exhibitorIdError={exhibitorIdError}
          eventCodeError={eventCodeError}
        />

        {readyForAuthentication && !isValid && (
          <Button
            alignSelf="flex-start"
            palette="primary"
            loading={loading}
            onClick={handleAPIAuth}
          >
            Authenticate
          </Button>
        )}

        {readyForAuthentication && isValid && (
          <Flex direction="column" gap={2}>
            <Flex gap={1} opacity={0.6}>
              <CheckCircleIcon fontSize="sm" />
              <Text textStyle="bodyXSmall">Authenticated</Text>
            </Flex>
            <Button
              variant="outline"
              leftIcon={<Cog6ToothIcon />}
              justifyContent="flex-start"
              onClick={() => updateShowFieldMapping(true)}
            >
              <Flex gap={1} align="center" flex={1}>
                <Text textStyle="bodySmall">Map Fields</Text>
                {!leadMapping && (
                  <Text textStyle="bodySmall" color="red">
                    (Required)
                  </Text>
                )}
                <Spacer />
                <ChevronRightIcon fontSize="sm" />
              </Flex>
            </Button>
          </Flex>
        )}
      </Flex>

      {!qrCode?.type && (
        <Button
          variant="outline"
          leftIcon={<EnvelopeIcon />}
          h="40px"
          onClick={() =>
            sendIntercomMessage(
              `Hello team, I need help with my QR service provider setup for the event ${eventTitle}!`
            )
          }
        >
          Reach out to Momentify team for setup support
        </Button>
      )}

      {qrCode && <QrMappingDrawer qrCode={qrCode} onUpdate={onConfigurationChange} />}
    </Flex>
  )
}

const getProviders = (
  showDemo: boolean
): { value: QRCodeProvider | 'not-listed'; label: string }[] => [
  ...(showDemo ? [{ value: 'demo' as const, label: 'Demo' }] : []),
  { value: 'maritz', label: 'Maritz' },
  { value: 'xpressleads', label: 'XPress Leads' },
  { value: 'converge', label: 'Converge' },
  { value: 'not-listed', label: 'Not Listed' },
]

interface ConfigurationOptionsRendererProps {
  qrCode?: QRCodeConfiguration
  onConfigurationChange?: (value: Partial<QRCodeConfiguration>) => void
  authKeyError: string
  actCodeError: string
  exhibitorBadgeIdError: string
  exhibitorIdError: string
  eventCodeError: string
}

function ConfigurationOptionsRenderer({
  qrCode,
  onConfigurationChange,
  authKeyError,
  actCodeError,
  exhibitorBadgeIdError,
  exhibitorIdError,
  eventCodeError,
}: ConfigurationOptionsRendererProps): ReactNode {
  if (!qrCode) return null

  const type = qrCode?.type
  switch (type) {
    case 'xpressleads':
      return (
        <Flex direction="column" gap={3}>
          <DebouncedTextField
            label="Authentication Key"
            id="authKey"
            value={qrCode.authKey || ''}
            placeholder="Please enter authentication key"
            onChange={value => onConfigurationChange?.({ authKey: value, isValid: false })}
            isError={!!authKeyError}
            helperText={authKeyError}
          />

          <DebouncedTextField
            label="Exhibitor ID"
            id="exhibitorId"
            value={qrCode.exhibitorId || ''}
            placeholder="Please enter exhibitor id"
            onChange={value => onConfigurationChange?.({ exhibitorId: value, isValid: false })}
            isError={!!exhibitorIdError}
            helperText={exhibitorIdError}
          />

          <DebouncedTextField
            label="Event Code"
            id="eventCode"
            value={qrCode.eventCode || ''}
            placeholder="Please enter event code"
            onChange={value => onConfigurationChange?.({ eventCode: value, isValid: false })}
            isError={!!eventCodeError}
            helperText={eventCodeError}
          />
        </Flex>
      )
    case 'converge':
      return (
        <Flex direction="column" gap={3}>
          <DebouncedTextField
            label="API Key"
            id="authKey"
            value={qrCode.apiKey || ''}
            placeholder="Please enter API key"
            onChange={value => onConfigurationChange?.({ apiKey: value, isValid: false })}
            isError={!!authKeyError}
            helperText={authKeyError}
          />
        </Flex>
      )
    case 'maritz':
      return (
        <Flex direction="column" gap={3}>
          <DebouncedTextField
            label="API Key"
            id="authKey"
            value={qrCode.apiKey || ''}
            placeholder="Please enter API key"
            onChange={value => onConfigurationChange?.({ apiKey: value, isValid: false })}
            isError={!!authKeyError}
            helperText={authKeyError}
          />
          <DebouncedTextField
            label="ACT (Activation) Code"
            id="actCode"
            value={qrCode.actCode || ''}
            placeholder="Please enter activation code"
            onChange={value => onConfigurationChange?.({ actCode: value, isValid: false })}
            isError={!!actCodeError}
            helperText={actCodeError}
          />
          <DebouncedTextField
            label="Exhibitor Badge ID"
            id="exhibitorBadgeId"
            value={qrCode.exhibitorBadgeId || ''}
            placeholder="Please enter exhibitor badge id"
            onChange={value => onConfigurationChange?.({ exhibitorBadgeId: value, isValid: false })}
            isError={!!exhibitorBadgeIdError}
            helperText={exhibitorBadgeIdError}
          />
        </Flex>
      )
  }
}
